import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Home from "@/pages/Home";
import EventsPage from "@/pages/EventsPage";
import EventDetail from "@/pages/EventDetail";
import ForOrganisers from "@/pages/ForOrganisers";
import ForBrands from "@/pages/ForBrands";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import OrganiserSignup from "@/pages/OrganiserSignup";
import AdminLogin from "@/pages/AdminLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import OrganizerDashboard from "@/pages/OrganizerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import QRScanner from "@/pages/QRScanner";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AuthCallback from "@/pages/AuthCallback";
import VipPage from "@/pages/VipPage";
import CommunityFeed from "@/pages/CommunityFeed";
import ContestsPage from "@/pages/ContestsPage";
import EcosystemPortal from "@/pages/EcosystemPortal";
import WorkWithEventom from "@/pages/WorkWithEventom";
import ComingSoon from "@/pages/ComingSoon";
import SplashScreen from "@/components/SplashScreen";
import PoliciesPage from "@/pages/PoliciesPage";
import AiGalleryPage from "@/pages/AiGalleryPage";
import ChatPage from "@/pages/ChatPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import CityEventsPage from "@/pages/CityEventsPage";
import AboutUs from "@/pages/AboutUs";
import NotFound from "@/pages/NotFound";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import ClubVIP from "@/pages/ClubVIP";
import AIPosterStudio from "@/pages/AIPosterStudio";
import EventRecap from "@/pages/EventRecap";
import Store from "@/pages/Store";
import { CityProvider } from "@/context/CityContext";
import { ThemeProvider } from "@/context/ThemeContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// AppRouter intercepts the URL fragment BEFORE regular routes run so we can process the Google
// session_id synchronously (prevents race conditions with AuthProvider's checkAuth).
function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Gate staff (ticket checkers) can only use the scanner.
  if (user?.role === "checker" && location.pathname !== "/scanner") {
    return <Navigate to="/scanner" replace />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/events/:id" element={<EventDetail />} />
      <Route path="/e/:slug" element={<EventDetail />} />
      <Route path="/experiences/:slug" element={<EventDetail />} />
      <Route path="/ai-photos" element={<Navigate to="/ai-gallery" replace />} />
      <Route path="/organisers" element={<ForOrganisers />} />
      <Route path="/brands" element={<ForBrands />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/organizer/signup" element={<OrganiserSignup />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/organizer" element={<OrganizerDashboard />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/scanner" element={<QRScanner />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/vip" element={<VipPage />} />
      <Route path="/community" element={<CommunityFeed />} />
      <Route path="/contests" element={<ContestsPage />} />
      <Route path="/brands-portal" element={<Navigate to="/brands" replace />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/ecosystem" element={<EcosystemPortal />} />
      <Route path="/work-with-eventom" element={<WorkWithEventom />} />
      <Route path="/coming-soon" element={<ComingSoon />} />
      <Route path="/ai-gallery" element={<AiGalleryPage />} />
      <Route path="/chat" element={<ChatPage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/city/:slug" element={<CityEventsPage />} />
      <Route path="/policies/:slug" element={<PoliciesPage />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/clubvip" element={<ClubVIP />} />
      {/* Iter 39: Match is now embedded inside Club VIP. Legacy /match redirects. */}
      <Route path="/match" element={<Navigate to="/clubvip" replace />} />
      {/* Iter 40: dedicated Events & Experiences page (was previously a home-page redirect). */}
      <Route path="/events" element={<EventsPage />} />
      <Route path="/biz-list" element={<Navigate to="/ecosystem" replace />} />
      <Route path="/ai-poster-studio" element={<AIPosterStudio />} />
      <Route path="/recap/:eventId" element={<EventRecap />} />
      <Route path="/store" element={<Store />} />
      <Route path="/shop" element={<Store />} />
      <Route path="/biz" element={<Navigate to="/organisers" replace />} />
      <Route path="/explore" element={<Navigate to="/#events-grid" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  // Reveal-on-scroll observer (bold visual reset). Custom cursor tracker was
  // removed in Iter 39 — it caused scroll-jank and visual glitches on some
  // devices. Reveal is pointer/motion-aware and gracefully no-ops otherwise.
  useEffect(() => {
    const rm = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let io;
    if (typeof IntersectionObserver !== "undefined" && !rm) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); } });
      }, { threshold: 0.12, rootMargin: "-40px 0px" });
      const mo = new MutationObserver(() => {
        document.querySelectorAll("[data-reveal]:not(.is-visible)").forEach((el) => io.observe(el));
      });
      document.querySelectorAll("[data-reveal]").forEach((el) => io.observe(el));
      mo.observe(document.body, { childList: true, subtree: true });
      return () => { io.disconnect(); mo.disconnect(); };
    }
    return () => {};
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <CityProvider>
            <BrowserRouter>
              <SplashScreen />
              <Toaster position="top-right" richColors closeButton toastOptions={{ style: { background: "#141422", border: "1px solid rgba(255,255,255,0.15)", color: "#F5F5F7" } }} />
              <AppRouter />
            </BrowserRouter>
          </CityProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
